// Map orchestrator. Single grid: persistent left sidebar + map canvas.
// Map area also hosts the floating timeline (centered) and the slide-in
// event-detail panel (right). URL-stateful filter/selection store via
// useUrlState.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { OutbreakMap } from '../components/outbreak-map';
import { TimelineScrubber } from '../components/timeline';
import { EventDetail } from '../components/event-detail';
import { Sidebar } from '../components/sidebar';
import { TopNav } from '../components/top-nav';
import { LegalGate } from '../components/legal-gate';
import { OnboardingTour } from '../components/onboarding-tour';
import { fetchOutbreaks, applyFilters } from '../lib/outbreaks';
import { useUrlState, readInitialCamera, isEmbedMode } from '../lib/url-state';
import { supabaseConfigured } from '../lib/supabase';
import { SeoHead } from '../components/seo-head';
import { mapPageJsonLd } from '../lib/seo';
import type { OutbreakWithSources } from '../types/supabase';

export function MapView() {
  const [outbreaks, setOutbreaks] = useState<OutbreakWithSources[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [{ filters, selectedId, viewMode }, update] = useUrlState();
  // Camera params (lat/lng/zoom) are read once at mount — landing-page CTAs
  // bring users in with `?lat=…&lng=…&zoom=…` to focus a region. Filters
  // continue to flow through useUrlState; camera is one-shot and shouldn't
  // re-fire on every filter change.
  const initialCameraRef = useMemo(() => readInitialCamera(), []);
  // Embed mode (?embed=1) means the SPA is mounted inside an iframe on
  // a landing page. Skip the legal gate and onboarding tour — the parent
  // page owns those — and dim down the brand watermark.
  const embed = useMemo(() => isEmbedMode(), []);

  // First-visit policy: if the user lands on / with no URL filters and has
  // never been here before, pre-apply Andes + active so the live MV Hondius
  // and Argentina ANDV cluster are immediately visible. Default releases
  // the moment they touch any filter (handled by URL writes flipping a
  // localStorage marker the next time we check).
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem('hantavirusmap.io:first-visit:v1');
      const sp = new URLSearchParams(window.location.search);
      const urlHasFilters = ['p', 's', 'st', 'c', 'd0', 'd1', 'm0', 'm1', 'y0', 'y1', 'id', 'lat', 'lng']
        .some(k => sp.has(k));
      if (!seen && !urlHasFilters) {
        update(prev => ({
          ...prev,
          filters: {
            ...prev.filters,
            pathogens: new Set(['andes']),
            statuses: new Set(['active']),
          },
        }));
      }
      // Mark visited regardless — once the user has been here once, no more
      // forced defaults.
      window.localStorage.setItem('hantavirusmap.io:first-visit:v1', new Date().toISOString());
    } catch { /* localStorage unavailable — silently skip */ }
    // Intentionally only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchOutbreaks();
        if (!cancelled) {
          setOutbreaks(rows);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'failed to load outbreaks');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => applyFilters(outbreaks, filters), [outbreaks, filters]);
  const selected = useMemo(
    () => (selectedId ? outbreaks.find(o => o.id === selectedId) ?? null : null),
    [outbreaks, selectedId],
  );

  const handleSelect = useCallback(
    (id: string | null) => update(prev => ({ ...prev, selectedId: id })),
    [update],
  );

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleSelect(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, handleSelect]);

  const tokenMissing = !import.meta.env.VITE_MAPBOX_TOKEN;

  if (!supabaseConfigured || tokenMissing) {
    return <SetupCard tokenMissing={tokenMissing} supabaseMissing={!supabaseConfigured} />;
  }

  return (
    <div
      className="relative h-screen w-screen overflow-hidden"
      style={{ background: '#070612' }}
    >
      <SeoHead
        title="hantavirusmap.io — every documented hantavirus outbreak, 1993–present"
        description="A free, source-cited map of every documented hantavirus outbreak from 1993 to the present. Curated from WHO, CDC, ECDC, PAHO, and peer-reviewed literature. Educational use only."
        canonical="/"
        jsonLd={[mapPageJsonLd()]}
      />
      {/* First-visit blocking acknowledgement. Self-dismisses after the user
          accepts and persists ack to localStorage so it never shows again.
          Suppressed in embed mode — parent landing page owns disclaimers. */}
      {!embed && <LegalGate />}
      {/* Optional, replayable feature tour. Auto-starts only after legal
          gate is accepted; otherwise the two would compete. Suppressed in
          embed mode where vertical space is precious and the parent page
          provides its own context. */}
      {!embed && <OnboardingTour />}
      <OutbreakMap
        outbreaks={visible}
        selectedId={selectedId}
        onSelect={handleSelect}
        viewMode={viewMode}
        initialCamera={initialCameraRef ?? undefined}
      />

      {/* radial vignette for cosmic depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.42) 100%)',
        }}
      />

      {/* Sidebar / view-toggle / timeline are all hidden in embed mode.
          A landing-page iframe is small and the map is the whole point;
          competing chrome covers the markers and obscures the map. The
          full SPA at / has all of these intact. */}
      {!embed && (
        <>
          {/* floating sidebar — top-left, inset, scrolls internally if tall */}
          <div className="pointer-events-none absolute left-4 top-4 z-20">
            <Sidebar
              outbreaks={outbreaks}
              visible={visible}
              filters={filters}
              onFilters={(next) => update(prev => ({ ...prev, filters: next }))}
              selectedId={selectedId}
              onSelect={handleSelect}
              viewMode={viewMode}
              onViewMode={(next) => update(prev => ({ ...prev, viewMode: next }))}
              loading={loading}
            />
          </div>

          {/* top-right nav — site links + tour. Hides when a detail panel
              is open so it doesn't compete for attention. Hidden on mobile
              to avoid stacking behind the sidebar header. */}
          {!selected && (
            <div className="pointer-events-none absolute top-4 right-4 z-20 hidden sm:block">
              <TopNav />
            </div>
          )}

          {/* floating timeline — centered at bottom. Compact px on mobile so
              preset chips don't clip; max-w-2xl on desktop. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3 sm:bottom-4 sm:px-4">
            <div data-tour="timeline" className="surface-glass pointer-events-auto w-full max-w-2xl">
              <TimelineScrubber
                value={filters.dayRange}
                onChange={(dayRange) =>
                  update(prev => ({ ...prev, filters: { ...prev.filters, dayRange } }))
                }
                outbreaks={outbreaks}
              />
            </div>
          </div>
        </>
      )}

      {/* event detail — full screen on mobile, floating card on desktop */}
      {selected && (
        <div className="pointer-events-none fixed inset-2 z-40 sm:absolute sm:inset-auto sm:right-4 sm:top-4">
          <EventDetail outbreak={selected} onClose={() => handleSelect(null)} />
        </div>
      )}

      {/* "Maps by Micky" credit, bottom-right above the Mapbox attribution.
          Hidden on mobile where vertical space is precious. In embed mode
          the credit becomes "View on hantavirusmap.io ↗" so users in the
          iframe have a one-click escape hatch back to the full site. */}
      <a
        href={embed ? '/' : 'https://atlasdevelopment.io/'}
        target={embed ? '_top' : '_blank'}
        rel="noopener noreferrer"
        className="pointer-events-auto absolute bottom-4 right-4 z-20 hidden h-10 items-center gap-1.5 rounded-full bg-[var(--color-accent)] px-5 text-[14px] font-semibold text-white shadow-sm transition-colors hover:bg-[var(--color-accent-hover)] sm:inline-flex"
      >
        <MapIcon size={15} strokeWidth={2.25} aria-hidden />
        {embed ? 'Open full map' : 'Map by Atlas'}
        <span aria-hidden className="text-white/70">↗</span>
      </a>

      {/* Loading is now surfaced by the sidebar events-list skeleton —
          no redundant corner popup. */}
      {error && <CornerStatus label={`Error: ${error}`} variant="danger" />}
      {!loading && !error && outbreaks.length === 0 && (
        <CornerStatus label="No outbreaks yet — run npm run seed" />
      )}

      <noscript>
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-text-2)]">
          this site requires JavaScript
        </div>
      </noscript>
    </div>
  );
}

function CornerStatus({ label, variant }: { label: string; variant?: 'danger' }) {
  return (
    <div
      className={
        'fade-in surface-glass absolute right-4 top-4 z-30 px-3 py-2 text-[12px] font-medium ' +
        (variant === 'danger' ? 'text-[var(--color-signal)]' : 'text-[var(--color-text-2)]')
      }
    >
      {label}
    </div>
  );
}

function SetupCard({ tokenMissing, supabaseMissing }: { tokenMissing: boolean; supabaseMissing: boolean }) {
  return (
    <div className="flex h-screen items-center justify-center bg-[var(--color-bg)] px-6">
      <div className="surface w-full max-w-md p-6">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
          Setup required
        </div>
        <h1 className="mb-3 text-[20px] font-semibold leading-snug text-[var(--color-text)]">
          Drop your env vars in
        </h1>
        <p className="mb-4 text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
          Create <code className="mono rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 text-[12px] text-[var(--color-text)]">frontend/.env.local</code> with:
        </p>
        <pre className="mono mb-4 overflow-x-auto rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] p-3 text-[12px] leading-relaxed text-[var(--color-text)]">
{tokenMissing && 'VITE_MAPBOX_TOKEN=pk.your-public-mapbox-token\n'}{supabaseMissing && 'VITE_SUPABASE_URL=https://your-project.supabase.co\nVITE_SUPABASE_ANON_KEY=your-anon-key\n'}
        </pre>
        <p className="text-[12px] text-[var(--color-text-3)]">
          See <code className="mono text-[var(--color-text)]">README.md</code> for full setup
        </p>
      </div>
    </div>
  );
}
